import type { Middleware, UnknownAction } from "@reduxjs/toolkit";

import { addPendingPatch, commitPendingPatch, discardPendingPatch } from "~/store/availabilitySlice";
import {
	addAssignedCountInFlight,
	clearAssignedCountInFlight,
	clearCountConfirmationWatch,
	confirmCountConfirmationWatch,
	watchCountConfirmation
} from "~/store/materialMobileSlice";

export const CONFIRMATION_SYNC_CHANNEL_NAME = "worksuite-confirmation-watch-sync";

const MIRRORED_ACTION_TYPES = new Set<string>([
	watchCountConfirmation.type,
	confirmCountConfirmationWatch.type,
	clearCountConfirmationWatch.type,
	addAssignedCountInFlight.type,
	clearAssignedCountInFlight.type,
	addPendingPatch.type,
	commitPendingPatch.type,
	discardPendingPatch.type
]);

interface RelayableAction extends UnknownAction {
	meta?: { crossTabRelay?: boolean } & Record<string, unknown>;
}

const isRelayedFromAnotherTab = (action: RelayableAction): boolean => action.meta?.crossTabRelay === true;

const asRelayableAction = (data: unknown): RelayableAction | undefined => {
	if (!data || typeof data !== "object") return undefined;
	const type = (data as { type?: unknown }).type;
	if (typeof type !== "string" || !MIRRORED_ACTION_TYPES.has(type)) return undefined;
	return data as RelayableAction;
};

export interface CrossTabConfirmationSync {
	channel: BroadcastChannel | undefined;
	middleware: Middleware;
}

export const createCrossTabConfirmationSync = (
	channelName: string = CONFIRMATION_SYNC_CHANNEL_NAME
): CrossTabConfirmationSync => {
	const channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel(channelName);

	const middleware: Middleware = (api) => {
		if (channel) {
			channel.onmessage = (event: MessageEvent<unknown>) => {
				const relayed = asRelayableAction(event.data);
				if (!relayed) return;
				api.dispatch({ ...relayed, meta: { ...relayed.meta, crossTabRelay: true } });
			};
		}

		return (next) => (rawAction) => {
			const action = rawAction as RelayableAction;
			const result = next(action);
			if (channel && MIRRORED_ACTION_TYPES.has(action.type) && !isRelayedFromAnotherTab(action)) {
				channel.postMessage(action);
			}
			return result;
		};
	};

	return { channel, middleware };
};

const defaultSync = createCrossTabConfirmationSync();

export default defaultSync.middleware;
